import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { PATHS } from '@/config/paths';
import {
  useGetAlertHistoryQuery,
  useGetEtlJobsQuery,
  useGetNetcdfJobsQuery,
} from '@/features/admin/api/opsManagementApi';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { requestOpenIotStationDetail, requestStationFlyTo } from '@/features/map/stores/mapSlice';
import { buildSeedHeaderNotificationItems } from '@/features/notifications/utils/buildSeedHeaderNotifications';
import {
  loadReadNotificationIds,
  saveReadNotificationIds,
} from '@/features/notifications/utils/headerNotificationReadStorage';
import {
  mapFailedEtlJobsToHeaderItems,
  mapFailedNetcdfJobsToHeaderItems,
  mapOpenAlertEventsToHeaderItems,
} from '@/features/notifications/utils/mapApiToHeaderNotifications';
import { useDashboardIoTStations } from '@/features/realtime/hooks/useDashboardIoTStations';
import { useAppDispatch } from '@/hooks/useAppDispatch';
import { shouldFetchOperationalApi } from '@/utils/dataSource';

import type { HeaderNotificationListItem } from '../types/headerNotification.types';

const ALERT_POLL_MS = 90_000;
const JOB_POLL_MS = 120_000;
const MERGED_LIMIT = 30;

/**
 * Tải và hợp nhất thông báo chuông (seed + cảnh báo mở + job thất bại từ API), trạng thái đã đọc lưu cục bộ theo user.
 */
export function useHeaderNotifications() {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const userId = user?.id ?? null;
  const operationalApi = shouldFetchOperationalApi();
  const { stations } = useDashboardIoTStations({ skip: !user || !operationalApi });

  const [readList, setReadList] = useState<string[]>(() => [...loadReadNotificationIds(userId)]);

  useEffect(() => {
    // Đổi tài khoản → nạp lại danh sách đã đọc từ storage (không remount dropdown để tránh lỗi RTKQ subscription).
    // eslint-disable-next-line react-hooks/set-state-in-effect -- chỉ chạy khi userId đổi, không phản ứng với render khác
    setReadList([...loadReadNotificationIds(userId)]);
  }, [userId]);

  const readIds = useMemo(() => new Set(readList), [readList]);

  const { data: alertPage } = useGetAlertHistoryQuery(
    { status: 'open', pageSize: 20, page: 1 },
    { skip: !user, pollingInterval: user ? ALERT_POLL_MS : 0 },
  );

  const { data: netcdfPage } = useGetNetcdfJobsQuery(
    { pageno: 1, pagesize: 40 },
    { skip: !user, pollingInterval: user ? JOB_POLL_MS : 0 },
  );
  const netcdfJobs = useMemo(() => netcdfPage?.items ?? [], [netcdfPage?.items]);
  const { data: etlJobs = [] } = useGetEtlJobsQuery(
    { limit: '40' },
    { skip: !user, pollingInterval: user ? JOB_POLL_MS : 0 },
  );

  const items: HeaderNotificationListItem[] = useMemo(() => {
    const read = readIds;
    const seeds = operationalApi ? [] : buildSeedHeaderNotificationItems(t, read);

    const apiAlerts = mapOpenAlertEventsToHeaderItems(alertPage?.items ?? [], t, i18n.language);
    const failedNet = mapFailedNetcdfJobsToHeaderItems(netcdfJobs, t, i18n.language);
    const failedEtl = mapFailedEtlJobsToHeaderItems(etlJobs, t, i18n.language);

    const byId = new Map<string, HeaderNotificationListItem>();

    for (const s of seeds) {
      byId.set(s.id, { ...s, read: read.has(s.id) });
    }
    for (const a of apiAlerts) {
      byId.set(a.id, { ...a, read: read.has(a.id) });
    }
    for (const j of failedNet) {
      byId.set(j.id, { ...j, read: read.has(j.id) });
    }
    for (const j of failedEtl) {
      byId.set(j.id, { ...j, read: read.has(j.id) });
    }

    const list = [...byId.values()].sort(
      (a, b) => new Date(b.occurredAt).getTime() - new Date(a.occurredAt).getTime(),
    );
    return list.slice(0, MERGED_LIMIT);
  }, [readIds, t, i18n.language, operationalApi, alertPage?.items, netcdfJobs, etlJobs]);

  const itemsRef = useRef(items);

  useEffect(() => {
    itemsRef.current = items;
  }, [items]);

  const unreadCount = useMemo(() => items.filter((x) => !x.read).length, [items]);

  const markAsRead = useCallback(
    (id: string) => {
      setReadList((prev) => {
        if (prev.includes(id)) return prev;
        const next = [...prev, id];
        saveReadNotificationIds(userId, new Set(next));
        return next;
      });
    },
    [userId],
  );

  const markAllRead = useCallback(() => {
    setReadList((prev) => {
      const s = new Set(prev);
      for (const it of itemsRef.current) {
        s.add(it.id);
      }
      const next = [...s];
      saveReadNotificationIds(userId, new Set(next));
      return next;
    });
  }, [userId]);

  const onSelectItem = useCallback(
    (item: HeaderNotificationListItem) => {
      markAsRead(item.id);
      if (item.actionKind === 'openIotStationDetail') {
        const targetId = item.stationId;
        const nameHint = item.iotStationNameContains?.trim();

        // Tìm trạm theo ID, mã trạm (sensorId) hoặc gợi ý tên (nameHint)
        const st = stations.find(
          (s) =>
            (targetId && (s.id === targetId || s.sensorId === targetId || s.name === targetId)) ||
            (nameHint && s.name?.includes(nameHint)),
        );

        if (st) {
          const sid = st.id;
          dispatch(requestOpenIotStationDetail({ stationId: sid }));
          dispatch(
            requestStationFlyTo({
              longitude: st.position[0],
              latitude: st.position[1],
              zoom: 15,
            }),
          );
          navigate(PATHS.MAP_IOT);
          return;
        }
        if (item.actionPath) {
          navigate(item.actionPath);
        }
        return;
      }
      if (item.actionPath) {
        navigate(item.actionPath);
      }
    },
    [markAsRead, navigate, stations, dispatch],
  );

  return {
    items,
    /** Số mục chưa đọc (dùng cho badge). */
    unreadCount,
    markAsRead,
    markAllRead,
    onSelectItem,
  };
}
